import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchOwnerWallets, resolveMainWalletForCurrency } from '@/store/slices/wallet';
import { selectNShowcase } from '@/store/slices/userPreferences';
import { selectSelectedCasinoCurrency } from '@/store/slices/casino-instance';

import { BrandSelector } from '../../../../components/BrandSelector/BrandSelector';
import { Icon } from '../../../../components';
import { WalletGlyph } from '../WalletGlyph';
import { CreditsChip } from '../CreditsChip';
import { NotificationBell } from '../NotificationBell';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import { currencySymbol, formatAmountWithCode, formatCompact } from '../../../../utils/money';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { MOBILE_MQ } from '../../../../theme/tokens';
import { useRouteLabel, routeAreaRoot } from '../../routeLabels';
import { useImmersive } from '../../immersiveControl';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { useLocalInternalSite } from './useLocalInternalSite';
import {
  Root,
  Breadcrumb,
  BackButton,
  NameInput,
  NameEditWrap,
  NameEditIcon,
  HomeLink,
  HomeIcon,
  SepDot,
  LabelLink,
  Label,
  Utils,
  WalletLink,
  WalletAmount,
  WalletCreditsSep,
  ClusterPush,
  LumioTrigger,
  InternalSiteLink,
  InternalSiteLabel,
} from './TopBar.styles';

/**
 * Small top header — converted from the page-layout template's `.pg-body-top`:
 * route-driven breadcrumb on the left, brand selector + wallet link on the
 * right. The wallet amount is bound to the `ownerWallet` slice (the template's
 * "Wallet Placeholder" text was module-driven).
 */
const HOME_ICON_SRC = 'https://micros-t.b-cdn.net/home-1768912435643.svg';
/* Same multicolour mark as the LumioChat panel header (LumioChat `LOGO_SRC`). */
const LUMIO_MARK_SRC = '/assets/lumio/lumio-mark.svg';
/* Lumio's place for the FTUX: its hello lands on this trigger (inert outside an FTUX step). With the dock open on wide
   desktop the trigger is hidden, and the docked panel's header mark carries the same id (LumioChat). */
const LUMIO_PLACE_TARGET: FtuxTargetId = 'lumio-place';

export type TopBarProps = {
  /** Opens the Lumio drawer. The trigger that calls it is shown only on the
   * mid-desktop range where the docked Lumio column is hidden (LUMIO_DRAWER_RANGE). */
  onOpenLumio: () => void;
  /** Whether the Lumio drawer is currently open — drives the trigger's
   * `aria-expanded` so assistive tech knows the control toggles a live overlay. */
  lumioOpen: boolean;
  /** Hide the header Lumio trigger entirely (e.g. on the Creatives surface, which has its own). */
  hideLumioTrigger?: boolean;
  /** Wide desktop: show the trigger too (the docked Lumio column is collapsed, so it reopens it). */
  showLumioTriggerOnDesktop?: boolean;
};

export const TopBar = ({
  onOpenLumio,
  lumioOpen,
  hideLumioTrigger = false,
  showLumioTriggerOnDesktop = false,
}: TopBarProps): React.ReactElement => {
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const notificationsOn = useFeatureGate().isEnabled('owner-notifications');
  // On the phone header the balance shares a tight row, so the figure is compacted ("$47.8K USD").
  const isMobile = useMediaQuery(MOBILE_MQ);
  const ownerWallet = useAppSelector((state) => state.ownerWallet ?? null);
  // The Newton showcase leaves the owner wallet balance (and its separator) out of the header; credits stay.
  const newtonShowcase = useAppSelector(selectNShowcase);
  // The wallet/credits separator shows only when the credits chip does (a real balance loaded).
  const showCreditsSep = useAppSelector((state) => (state.ownerWallet?.credits ?? null) !== null);

  // Fetch once when nobody has, and NOT while a read is already in flight: `pending` clears
  // `loaded`, so keying on that alone makes every wallet read anywhere in the app trigger a
  // second one from here. The post-deposit balance watch reads ~20 times in a row, which
  // turned into ~40 requests against a paginated endpoint.
  React.useEffect(() => {
    if (!ownerWallet || (!ownerWallet.loaded && !ownerWallet.loading)) {
      dispatch(fetchOwnerWallets());
    }
  }, [ownerWallet, dispatch]);

  // Local Studio only: the active brand's site at its internal host, beside the brand selector (whose own button opens
  // the public domain, usually not connected off production).
  const activeBrand = useAppSelector((state) => state.casinoInstance.activeBrand);
  const activeTenant = Number(activeBrand?.tenant_id);
  const internalSite = useLocalInternalSite(
    Number.isFinite(activeTenant) && activeTenant > 0 ? activeTenant : undefined,
    activeBrand?.casino_data?.details?.domain?.trim() ?? '',
  );

  const label = useRouteLabel(location.pathname);
  const areaRoot = routeAreaRoot(location.pathname);
  // In immersive (full-screen) mode the side menu is slid out; a back button in the breadcrumb row restores
  // it (and runs the page's onExit), sitting on the same line as the "· Creatives" label instead of floating.
  const { immersive, exitImmersive, titleEditor } = useImmersive();
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  // Header shows the AVAILABLE (withdrawable) balance = the MAIN wallet, matching the Wallet
  // page. Sourced in the SELECTED CASINO's currency (falling back to the primary MAIN wallet in
  // its own currency) so the header, the Wallet page, and the pay flows all read the same figure.
  // A budget top-up already DEBITs MAIN (it's a transfer to the acquisition wallet), so `main` is
  // net of committed ad budgets — subtracting acquisition again would double-count money that
  // already left MAIN. `resolveMainWalletForCurrency` clamps at 0 as a NaN/negative guard.
  const casinoCurrency = useAppSelector(selectSelectedCasinoCurrency);
  const walletView = resolveMainWalletForCurrency(ownerWallet, casinoCurrency);

  return (
    <Root>
      <Breadcrumb>
        {immersive ? (
          <BackButton type="button" aria-label={t('Exit full screen')} onClick={exitImmersive}>
            <Icon name="arrow-left" size={18} />
          </BackButton>
        ) : null}
        {titleEditor ? (
          <NameEditWrap>
            <NameInput
              ref={nameInputRef}
              value={titleEditor.value}
              onChange={(e) => titleEditor.onChange(e.target.value)}
              placeholder={t('Name this video ad')}
              aria-label={t('Video ad name')}
            />
            <NameEditIcon type="button" aria-label={t('Edit name')} onClick={() => nameInputRef.current?.focus()}>
              <Icon name="edit" size={14} />
            </NameEditIcon>
          </NameEditWrap>
        ) : (
          <>
            <HomeLink to="/account/home" aria-label={t('Home')}>
              <HomeIcon $src={HOME_ICON_SRC} />
            </HomeLink>
            <SepDot className="fi fi-dot" aria-hidden="true" />
            <LabelLink to={areaRoot}>
              <Label text={label} />
            </LabelLink>
          </>
        )}
      </Breadcrumb>
      <Utils>
        <BrandSelector />
        {internalSite ? (
          <InternalSiteLink
            href={internalSite}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('Open the internal site in a new tab')}
            title={internalSite}
          >
            <Icon name="external-link" size={14} />
            <InternalSiteLabel text="Internal site" />
          </InternalSiteLink>
        ) : null}
        {newtonShowcase ? (
          <ClusterPush aria-hidden="true" />
        ) : (
          <WalletLink to="/account/wallet">
            <WalletGlyph size={20} />
            {/* Money figure is a data value, not copy — rendered through <Text> as-is. */}
            <WalletAmount
              text={
                ownerWallet?.loaded && walletView.balance !== null
                  ? isMobile
                    ? `${currencySymbol(walletView.currency)}${formatCompact(walletView.balance)} ${walletView.currency}`
                    : `${currencySymbol(walletView.currency)}${formatAmountWithCode(walletView.balance, walletView.currency)}`
                  : '–'
              }
            />
          </WalletLink>
        )}
        {/* AI-credits balance, to the right of the wallet (self-gating: absent until the
            credits backend answers, so it never shows a placeholder count), with a subtle
            hairline separating the two figures. */}
        {showCreditsSep && !newtonShowcase ? <WalletCreditsSep aria-hidden="true" /> : null}
        <CreditsChip />
        {/* What the partner's team sent the owner (owner-hub docs/owner-notifications.md), behind its flag. */}
        {notificationsOn ? <NotificationBell /> : null}
        {/* Opens Lumio wherever no Lumio surface is already on screen: the drawer on
            mid-desktop (the docked column is hidden there), the full-screen overlay on
            phones (the bottom bar is navigation only). Hidden on wide desktop while the
            column is open, and wherever the surface owns its own Lumio. */}
        {hideLumioTrigger ? null : (
          <LumioTrigger
            $desktop={showLumioTriggerOnDesktop}
            type="button"
            onClick={onOpenLumio}
            aria-label={t('Open Lumio')}
            title={t('Open Lumio')}
            aria-haspopup="dialog"
            aria-expanded={lumioOpen}
            data-ftux={LUMIO_PLACE_TARGET}
            // FTUX testers (777ftuxqa): lifted over the flow's layers while it holds the screen (inert otherwise).
            data-ftux-qa-lift=""
          >
            {/* Multicolour gradient mark — not recoloured, so a plain <img> (no
                fetch-and-inline) renders it; the gradient stays intact. */}
            <img src={LUMIO_MARK_SRC} alt="" />
          </LumioTrigger>
        )}
      </Utils>
    </Root>
  );
};

export default TopBar;
